/**
 * Known gaps in the declarative wire/effects layer (documented — no fixes until a real use case requires them):
 *
 * 1. Refresh effects may fire before the target block has registered its handle.
 *    handleRefresh() calls registry.get(targetId) synchronously; if the block is not mounted yet,
 *    the refresh is silently dropped (unlike command/param protocols, refresh does not queue pending work).
 *
 * 2. No timeout mechanism for effects that target non-existent blocks.
 *    pendingCommands for command/param are replayed on registration, but there is no expiry — a block
 *    that never mounts leaves stale pending state until EffectInterpreter.dispose().
 *
 * 3. Effect ordering is not guaranteed when multiple effects fire simultaneously.
 *    Bus delivery and debounced refresh scheduling are independent per effect; concurrent source events
 *    can cause target reactions to run in non-deterministic order relative to each other.
 */

import type {
  EffectDeclaration,
  BusEvent,
  Unsubscribe,
  AnyBlockHandle,
  TableBlockHandle,
  FormBlockHandle,
  ViewBlockHandle,
  TabBlockHandle,
  BlockReaction,
} from '../core/types';
import type { EventScope } from '../core/EventScope';
import { RefreshDeduplicator } from './RefreshDeduplicator';
import { MappingEngine } from './MappingEngine';
import { ConditionEngine } from './ConditionEngine';
import { CycleDetector } from './CycleDetector';

interface PendingCommand {
  reaction: BlockReaction;
  args?: Record<string, unknown>;
}

export class EffectInterpreter {
  private readonly scope: EventScope;
  private readonly effects: EffectDeclaration[];
  private readonly subscriptions: Unsubscribe[] = [];
  private readonly deduplicator = new RefreshDeduplicator();
  private readonly mappingEngine = new MappingEngine();
  private readonly conditionEngine = new ConditionEngine();
  private readonly cycleDetector = new CycleDetector();
  private readonly pendingCommands = new Map<string, PendingCommand>();
  private readonly derivedPayloads = new Map<string, Record<string, unknown>>();
  private disposed = false;

  constructor(scope: EventScope, effects: EffectDeclaration[]) {
    this.scope = scope;
    this.effects = effects;
    this.wireEffects();
    this.wireRegistrationCallbacks();
  }

  private wireEffects(): void {
    for (const effect of this.effects) {
      const pattern = `${effect.source.block}:${effect.source.event}`;

      const unsub = this.scope.bus.subscribe(pattern, (event: BusEvent) => {
        this.handleEvent(effect, event);
      });

      this.subscriptions.push(unsub);
    }
  }

  private wireRegistrationCallbacks(): void {
    const targetIds = new Set(this.effects.map((e) => e.target.block));

    for (const targetId of targetIds) {
      const unsub = this.scope.registry.onRegister(targetId, (handle) => {
        this.onTargetRegistered(targetId, handle);
      });
      this.subscriptions.push(unsub);
    }
  }

  private onTargetRegistered(targetId: string, handle: AnyBlockHandle): void {
    const pending = this.pendingCommands.get(targetId);
    if (pending) {
      this.executeReaction(handle, pending.reaction, pending.args);
      this.pendingCommands.delete(targetId);
    }

    const derivedPayload = this.derivedPayloads.get(targetId);
    if (derivedPayload) {
      const derivedEffects = this.effects.filter(
        (e) => e.target.block === targetId && e.protocol === 'derived',
      );
      for (const effect of derivedEffects) {
        const args = this.mappingEngine.resolve(effect.mapping, derivedPayload);
        this.executeReaction(handle, effect.target.reaction, args);
      }
    }
  }

  private handleEvent(effect: EffectDeclaration, event: BusEvent): void {
    if (this.disposed) return;

    const payload = event.payload as Record<string, unknown>;

    if (effect.source.filter) {
      if (!this.conditionEngine.evaluateFilter(effect.source.filter, payload)) {
        return;
      }
    }

    if (effect.when) {
      if (!this.conditionEngine.evaluateCondition(effect.when, payload)) {
        return;
      }
    }

    if (!this.cycleDetector.enter(event.sourceBlock, event.type)) {
      return;
    }

    try {
      const args = this.mappingEngine.resolve(effect.mapping, payload);

      switch (effect.protocol) {
        case 'refresh':
          this.handleRefresh(effect, args);
          break;
        case 'param':
          this.handleParam(effect, args);
          break;
        case 'command':
          this.handleCommand(effect, args);
          break;
        case 'derived':
          this.handleDerived(effect, payload, args);
          break;
      }

      if (import.meta.env.DEV) {
        console.debug(
          `[block-events] EFFECT ${effect.id}: ${effect.protocol} → ${effect.target.block}.${effect.target.reaction}`,
        );
      }
    } finally {
      this.cycleDetector.exit();
    }
  }

  private handleRefresh(
    effect: EffectDeclaration,
    args: Record<string, unknown> | undefined,
  ): void {
    const { block: targetId, reaction } = effect.target;
    const debounceMs = effect.debounceMs ?? 100;

    const execute = () => {
      const handle = this.scope.registry.get(targetId);
      if (handle) {
        this.executeReaction(handle, reaction, args);
      }
    };

    if (debounceMs > 0) {
      this.deduplicator.schedule(targetId, execute);
    } else {
      execute();
    }
  }

  private handleParam(
    effect: EffectDeclaration,
    args: Record<string, unknown> | undefined,
  ): void {
    const { block: targetId, reaction } = effect.target;

    if (args) {
      this.scope.params.set(targetId, args);
    }

    const handle = this.scope.registry.get(targetId);
    if (handle) {
      this.executeReaction(handle, reaction, args);
    } else if (args) {
      this.pendingCommands.set(targetId, { reaction, args });
    }
  }

  private handleCommand(
    effect: EffectDeclaration,
    args: Record<string, unknown> | undefined,
  ): void {
    const { block: targetId, reaction } = effect.target;

    const handle = this.scope.registry.get(targetId);
    if (handle) {
      this.executeReaction(handle, reaction, args);
    } else {
      this.pendingCommands.set(targetId, { reaction, args });
    }
  }

  private handleDerived(
    effect: EffectDeclaration,
    payload: Record<string, unknown>,
    args: Record<string, unknown> | undefined,
  ): void {
    const { block: targetId, reaction } = effect.target;

    this.derivedPayloads.set(targetId, payload);

    const handle = this.scope.registry.get(targetId);
    if (handle) {
      this.executeReaction(handle, reaction, args);
    }
  }

  private executeReaction(
    handle: AnyBlockHandle,
    reaction: BlockReaction,
    args: Record<string, unknown> | undefined,
  ): void {
    if (!handle.capabilities.has(reaction)) {
      if (import.meta.env.DEV) {
        console.warn(
          `[block-events] Block "${handle.semanticId}" does not support reaction "${reaction}". Skipped.`,
        );
      }
      return;
    }

    switch (reaction) {
      case 'refreshData':
        (handle as TableBlockHandle).refreshData();
        break;
      case 'resetSelection':
        (handle as TableBlockHandle).resetSelection();
        break;
      case 'setFilter':
        if (args) (handle as TableBlockHandle).setFilter(args);
        break;
      case 'setParams':
        if (args) (handle as TableBlockHandle).setParams(args);
        break;
      case 'submitForm':
        void (handle as FormBlockHandle).submitForm();
        break;
      case 'patchValues':
        if (args) (handle as FormBlockHandle).patchValues(args);
        break;
      case 'resetForm':
        (handle as FormBlockHandle).resetForm(args);
        break;
      case 'refetch':
        (handle as ViewBlockHandle).refetch();
        break;
      case 'setBadge':
        if (args) {
          (handle as TabBlockHandle).setBadge(
            args.tabId as string,
            args.value as string | number | null,
          );
        }
        break;
      case 'setActiveTab':
        if (args) (handle as TabBlockHandle).setActiveTab(args.tabId as string);
        break;
      case 'custom':
        break;
    }
  }

  dispose(): void {
    this.disposed = true;
    for (const unsub of this.subscriptions) unsub();
    this.subscriptions.length = 0;
    this.deduplicator.dispose();
    this.pendingCommands.clear();
    this.derivedPayloads.clear();
  }
}
